<template>
  <DrawerVue :refreshDisplay="refreshDisplay">
    <v-container fluid class="mt-4">
      <v-row class="mb-4" align="center">
        <v-col cols="auto">
          <h2>{{ $t(productTypeData ? 'basics.edit' : 'basics.new', { type: $t('basics.productType') }) }}</h2>
        </v-col>
        <v-spacer />
        <!-- Permission name to match the forthcoming backend view. -->
        <v-col cols="auto"
          v-if="productTypeData && !readonly && $checkFunctionPermission(['producttype_delete_detail'])">
          <v-tooltip :text="$t('basics.delete')">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" @click="$emit('delete')" class="icon-transparency hoverable">mdi-trash-can</v-icon>
            </template>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-form ref="productTypeEditForm" :disabled="loading">
        <v-row><v-col cols="12">
            <v-text-field variant="outlined" v-model="name" :rules="[requiredRule, max256]"
              :label="$t('basics.name') + '*'" :readonly="readonly" />
          </v-col></v-row>
        <v-row><v-col cols="12">
            <v-textarea variant="outlined" v-model="description" :label="$t('basics.description')"
              :readonly="readonly" />
          </v-col></v-row>
        <v-row><v-col cols="12">
            <v-text-field variant="outlined" v-model="colorCode" :rules="[colorRule]" :label="$t('basics.color') + '*'"
              :readonly="readonly">
              <template v-slot:prepend-inner><v-icon
                  :color="/^#[0-9a-f]{6}$/i.test(colorCode) ? colorCode : undefined">mdi-circle</v-icon></template>
            </v-text-field>
            <v-color-picker style="width: 100%" v-if="!readonly" v-model="colorCode" mode="hex" :modes="['hex']"
              hide-inputs hide-mode-switch />
          </v-col></v-row>
        <v-row><v-col cols="12">
            <v-text-field variant="outlined" type="number" min="0" max="99999.99" step="0.01" v-model="defaultPrice"
              :rules="[requiredRule, priceRule]" :label="$t('basics.defaultPrice') + ' ' + $t('basics.net') + '*'"
              :readonly="readonly" />
          </v-col></v-row>
        <v-row><v-col cols="12">
            <v-text-field variant="outlined" type="number" min="0" max="32767" step="1" v-model="defaultDuration"
              :rules="[durationRule]" :label="$t('basics.defaultDuration')" :readonly="readonly" />
          </v-col></v-row>
        <!-- <v-row><v-col cols="12">
          <v-select variant="outlined" :items="availPaymentMethods" v-model="defaultPaymentMethod" :item-title="item => $t('constants.payment_method_' + item.name)" item-value="key" :label="$t('basics.defaultPaymentMethod')" :readonly="readonly" clearable />
        </v-col></v-row> -->
        <v-row><v-col cols="12">
            <v-checkbox class="hideDetails" v-model="isPsycotherapy" :label="$t('messages.isPsycotherapy')"
              :readonly="readonly" />
          </v-col></v-row>
        <v-row><v-col cols="12">
            <v-file-input v-if="!readonly" variant="outlined" v-model="thumbnailFile" accept="image/*"
              :label="$t('basics.thumbnail')" :rules="[imageRule]" @update:model-value="updateThumbnailPreview" />
            <div style="position: relative">
              <v-img v-if="thumbnailPreview" :src="thumbnailPreview" contain class="rounded-lg" />
              <v-btn style="position: absolute; top: 0; right: 0; background-color: rgb(var(--v-theme-drawer)); border-top-right-radius: 0px !important; border-top-left-radius: 0px !important"
                v-if="thumbnailPreview && !readonly" variant="icon" @click="removeThumbnail"><v-icon
                  size="x-large">mdi-trash-can</v-icon></v-btn>
            </div>
          </v-col></v-row>
        <v-row v-if="!readonly"><v-col cols="12">
            <v-btn block flat class="primaryButton" :loading="loading" @click="confirm">{{ $t('basics.confirm')
            }}</v-btn>
          </v-col></v-row>
      </v-form>
    </v-container>
  </DrawerVue>
</template>

<script>
export default {
  name: 'productTypeEditDrawer',
  props: ['productTypeData', 'refreshDisplay', 'readonly'],
  emits: ['close', 'delete'],
  data() {
    return {
      name: '', description: '', colorCode: '#1976D2', defaultPrice: '0.00',
      defaultDuration: null, defaultPaymentMethod: null, isPsycotherapy: false,
      thumbnailFile: null, thumbnailPreview: null, thumbnailObjectUrl: null,
      thumbnailRemoved: false, availPaymentMethods: [], loading: false,
      requiredRule: value => (value !== null && value !== undefined && String(value).trim() !== '') || this.$t('messages.required'),
      max256: value => !value || value.length <= 256 || this.$t('messages.tooLong', { count: 256 }),
      colorRule: value => /^#[0-9a-f]{6}$/i.test(value || '') || 'Bitte einen HEX-Farbcode wie #1976D2 eingeben.',
      priceRule: value => (Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= 99999.99 && /^\d+(\.\d{1,2})?$/.test(String(value))) || 'Bitte einen Preis zwischen 0 und 99999,99 mit maximal zwei Nachkommastellen eingeben.',
      durationRule: value => value === '' || value === null || value === undefined || (Number.isInteger(Number(value)) && Number(value) >= 0 && Number(value) <= 32767) || 'Bitte eine ganze Zahl zwischen 0 und 32767 eingeben.',
      imageRule: value => {
        const file = Array.isArray(value) ? value[0] : value;
        return !file || file.type.startsWith('image/') || 'Bitte eine Bilddatei auswählen.';
      },
    };
  },
  watch: {
    async refreshDisplay(value) {
      if (!value) return;
      this.setValues();
      await this.fetchPaymentMethods();
    },
  },
  beforeUnmount() { this.revokeThumbnailPreview(); },
  methods: {
    showError(error) {
      window.dispatchEvent(new CustomEvent('showErrorDisplay', { detail: error }));
    },
    async fetchPaymentMethods() {
      try {
        const response = await this.axios.get(this.axios.defaults.baseURL + 'media/paymentmethods/');
        this.availPaymentMethods = response.data;
      } catch (error) { this.showError(error); }
    },
    revokeThumbnailPreview() {
      if (this.thumbnailObjectUrl) URL.revokeObjectURL(this.thumbnailObjectUrl);
      this.thumbnailObjectUrl = null;
    },
    updateThumbnailPreview(value) {
      this.revokeThumbnailPreview();
      const file = Array.isArray(value) ? value[0] : value;
      if (file && file.type.startsWith('image/')) {
        this.thumbnailObjectUrl = URL.createObjectURL(file);
        this.thumbnailPreview = this.thumbnailObjectUrl;
        this.thumbnailRemoved = false;
      } else {
        this.thumbnailPreview = this.thumbnailRemoved ? null : this.productTypeData?.thumbnail || null;
      }
    },
    removeThumbnail() {
      this.revokeThumbnailPreview();
      this.thumbnailFile = null;
      this.thumbnailPreview = null;
      this.thumbnailRemoved = true;
    },
    setValues() {
      const data = this.productTypeData;
      this.revokeThumbnailPreview();
      this.name = data?.name ?? '';
      this.description = data?.description ?? '';
      this.colorCode = data?.color_code ?? '#1976D2';
      this.defaultPrice = data?.default_price ?? '0.00';
      this.defaultDuration = data?.default_duration ?? null;
      this.defaultPaymentMethod = data?.default_payment_method ?? null;
      this.isPsycotherapy = data?.is_psycotherapy ?? false;
      this.thumbnailFile = null;
      this.thumbnailPreview = data?.thumbnail || null;
      this.thumbnailRemoved = false;
      this.$nextTick(() => this.$refs.productTypeEditForm?.resetValidation());
    },
    async confirm() {
      if (this.readonly || this.loading) return;
      const { valid } = await this.$refs.productTypeEditForm.validate();
      if (!valid) return;
      this.loading = true;
      try {
        // API contract to implement next: multipart POST/PATCH, empty values
        // for nullable defaults; empty thumbnail explicitly removes the image.
        const payload = new FormData();
        payload.append('name', this.name.trim());
        payload.append('description', this.description || '');
        payload.append('color_code', this.colorCode);
        payload.append('default_price', Number(this.defaultPrice).toFixed(2));
        payload.append('default_duration', this.defaultDuration === '' || this.defaultDuration == null ? '' : String(Number(this.defaultDuration)));
        payload.append('default_payment_method', this.defaultPaymentMethod ?? '');
        payload.append('is_psycotherapy', this.isPsycotherapy ? 'true' : 'false');
        const file = Array.isArray(this.thumbnailFile) ? this.thumbnailFile[0] : this.thumbnailFile;
        if (file) payload.append('thumbnail', file);
        else if (this.thumbnailRemoved) payload.append('thumbnail', '');
        const base = this.axios.defaults.baseURL + 'media/producttypes/';
        if (this.productTypeData) {
          await this.axios.patch(base + 'patch/' + this.productTypeData.id, payload);
        } else {
          await this.axios.post(base + 'post/', payload);
        }
        this.$emit('close', 1);
      } catch (error) { this.showError(error); }
      finally { this.loading = false; }
    },
  },
};
</script>
